/**
 * Voice-to-text for the interaction description, and nowhere else.
 *
 * **Privacy, stated plainly:** this uses the browser's own Web Speech API. No
 * audio ever reaches a MeshHub server — the CRM service and the BFF never see
 * it and have no code that could. What the browser itself does with the audio
 * is between the user and their browser vendor (Chrome, for instance, sends it
 * to Google's speech service). Only the finished text is submitted, and only
 * when the user saves the interaction. This is repeated in root CLAUDE.md.
 *
 * **Feature detection, not a polyfill.** Firefox has no SpeechRecognition, so
 * the control is absent there rather than present and broken. `isVoiceSupported`
 * is exported separately so a caller can decide *not to render* rather than
 * render a disabled button — a greyed-out mic invites clicks that go nowhere.
 */
import { Mic, MicOff } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { S } from './strings.js';

/** Persian. Configured here, once, like every other locale decision. */
const RECOGNITION_LANG = 'fa-IR';

function recognitionConstructor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

/** True when this browser can transcribe speech at all. */
export function isVoiceSupported() {
  return recognitionConstructor() !== null;
}

const ERROR_MESSAGES = {
  'not-allowed': S.voice.denied,
  'service-not-allowed': S.voice.denied,
  'no-speech': S.voice.noSpeech,
};

/**
 * Dictation as a hook.
 *
 * @param onTranscript called with the FINAL text of each utterance, to append
 * @param onInterim    called with the in-progress text, to preview
 */
export function useDictation({ onTranscript, onInterim }) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  // Refs, not deps: a caller that recreates its handlers each render (the
  // normal case) would otherwise tear down and restart recognition mid-sentence.
  const handlers = useRef({ onTranscript, onInterim });
  handlers.current = { onTranscript, onInterim };

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Recognition = recognitionConstructor();
    if (!Recognition) return;

    setError(null);
    const recognition = new Recognition();
    recognition.lang = RECOGNITION_LANG;
    recognition.continuous = true;
    // Interim results are what make dictation feel live: the user sees words
    // appear as they speak instead of after a pause.
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) {
          handlers.current.onTranscript?.(text.trim());
        } else {
          interim += text;
        }
      }
      handlers.current.onInterim?.(interim.trim());
    };

    recognition.onerror = (event) => {
      // 'aborted' is what stop() produces — not something to show the user.
      if (event.error === 'aborted') return;
      setError(ERROR_MESSAGES[event.error] ?? S.voice.failed);
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
      handlers.current.onInterim?.('');
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, []);

  // Unmounting mid-sentence must release the microphone, or the browser keeps
  // its indicator lit long after the dialog has closed.
  useEffect(() => () => recognitionRef.current?.abort(), []);

  return { listening, error, start, stop, toggle: () => (listening ? stop() : start()) };
}

/**
 * The mic toggle. Renders NOTHING when the browser cannot transcribe.
 *
 * Deliberately not a design-system component: dictation is a MeshHub product
 * decision about one field, not a reusable primitive.
 */
export function VoiceButton({ listening, onToggle, disabled }) {
  if (!isVoiceSupported()) return null;

  const Icon = listening ? MicOff : Mic;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={listening}
      aria-label={listening ? S.voice.stop : S.voice.start}
      title={listening ? S.voice.stop : S.voice.start}
      className={`inline-flex h-control items-center gap-2 rounded border px-3 text-sm
                  transition-colors disabled:opacity-50 ${
                    listening
                      ? 'border-danger bg-surface-hover text-danger'
                      : 'border-border bg-surface text-text-muted hover:bg-surface-hover'
                  }`}
    >
      <Icon size={14} aria-hidden="true" />
      {listening ? S.voice.listening : S.voice.start}
    </button>
  );
}
